/**
 * Hook React pour gérer les tokens CSRF
 * Récupère automatiquement le token et le configure dans Axios
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const CSRF_HEADER_NAME = 'X-CSRF-Token';
const CSRF_COOKIE_NAME = 'csrf_token';

// Fonction pour lire un cookie
function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
}

export function useCsrf() {
  const [csrfToken, setCsrfToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Récupérer le token CSRF
  const fetchCsrfToken = useCallback(async () => {
    try {
      // D'abord vérifier si on a déjà un cookie
      const existingToken = getCookie(CSRF_COOKIE_NAME);
      if (existingToken) {
        setCsrfToken(existingToken);
        setLoading(false);
        return existingToken;
      }

      // Sinon, en demander un nouveau
      const response = await axios.get(`${API_URL}/api/csrf/token`, {
        withCredentials: true
      });
      
      const token = response.data.csrf_token;
      setCsrfToken(token);
      setLoading(false);
      return token;
    } catch (err) {
      console.error('Error fetching CSRF token:', err);
      setError(err);
      setLoading(false);
      return null;
    }
  }, []);

  // Rafraîchir le token (après login par exemple)
  const refreshCsrfToken = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/csrf/refresh`, {}, {
        withCredentials: true
      });
      const token = response.data.csrf_token;
      setCsrfToken(token);
      setLoading(false);
      return token;
    } catch (err) {
      console.error('Error refreshing CSRF token:', err);
      setError(err);
      setLoading(false);
      return null;
    }
  }, []);

  // Récupérer le token au montage
  useEffect(() => {
    fetchCsrfToken();
  }, [fetchCsrfToken]);

  return {
    csrfToken,
    loading,
    error,
    fetchCsrfToken,
    refreshCsrfToken,
    headerName: CSRF_HEADER_NAME
  };
}

/**
 * Configure Axios pour envoyer automatiquement le token CSRF
 * À appeler une fois au démarrage de l'application
 */
export function setupCsrfInterceptor() {
  // Intercepteur pour ajouter le token CSRF aux requêtes
  axios.interceptors.request.use(
    (config) => {
      // Ajouter le token CSRF pour les requêtes mutatives
      const method = config.method?.toUpperCase();
      if (['POST', 'PUT', 'DELETE', 'PATCH'].includes(method)) {
        const csrfToken = getCookie(CSRF_COOKIE_NAME);
        if (csrfToken) {
          config.headers[CSRF_HEADER_NAME] = csrfToken;
        }
      }
      
      // S'assurer que les credentials sont envoyés (pour les cookies)
      config.withCredentials = true;
      
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Intercepteur pour gérer les erreurs CSRF
  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      // Si erreur CSRF, essayer de rafraîchir le token
      if (error.response?.status === 403 && 
          error.response?.data?.detail?.includes('CSRF')) {
        console.warn('CSRF token error, attempting refresh...');
        
        try {
          // Rafraîchir le token
          await axios.get(`${API_URL}/api/csrf/token`, {
            withCredentials: true
          });
          
          // Réessayer la requête originale
          const originalRequest = error.config;
          const newToken = getCookie(CSRF_COOKIE_NAME);
          if (newToken) {
            originalRequest.headers[CSRF_HEADER_NAME] = newToken;
          }
          return axios(originalRequest);
        } catch (refreshError) {
          console.error('Failed to refresh CSRF token:', refreshError);
        }
      }
      
      return Promise.reject(error);
    }
  );
}

export default useCsrf;
